import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VideoFeatures } from '@/store/videoFeatures/types';
import { SpeedTestRetentionChart } from './metrics/SpeedTestRetentionChart';
import { SpeedTestMetricsView } from '@/components/feature-configs/speed-testing/SpeedTestMetricsView';
import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp } from 'lucide-react';

interface SpeedTestMetricsSubTabProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const SpeedTestMetricsSubTab: React.FC<SpeedTestMetricsSubTabProps> = ({
  features,
}) => {
  useLanguage();
  // Buscar teste automático ativo
  const activeTest = features.speedTests?.find(test => test.enabled && test.isAutomatic);
  const hasActiveTest = !!activeTest && activeTest.testStatus === 'running';

  if (!hasActiveTest || !activeTest) {
    return (
      <div className="py-4 space-y-6">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-foreground">{translateInterfaceText("Métricas de Teste")}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {translateInterfaceText("Nenhum teste ativo no momento")}</p>
        </div>

        <Card className="border-[#E6E6E6]">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <TrendingUp className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h4 className="text-lg font-semibold text-foreground mb-2">
                {translateInterfaceText("Nenhum teste em execução")}</h4>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {translateInterfaceText("Vá para a aba \"Automático\" e ative um teste de velocidade para ver as métricas aqui.")}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="py-4 space-y-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground">{translateInterfaceText("Métricas de Teste")}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {translateInterfaceText("Atualização em tempo real dos resultados do teste")}</p>
      </div>

      {/* Tabs internas com estilo premium azul — mesmo padrão do HeadlineMetricsTab. */}
      <Tabs defaultValue="metrics" className="w-full">
        <TabsList
          className="grid w-full grid-cols-2 h-11 bg-[#F1F5F9] p-1 rounded-xl"
          style={{ minWidth: '300px' }}
        >
          <TabsTrigger
            value="metrics"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Métricas")}</TabsTrigger>
          <TabsTrigger
            value="retention"
            className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
          >
            {translateInterfaceText("Retenção")}</TabsTrigger>
        </TabsList>

        <TabsContent value="metrics" className="mt-6">
          <SpeedTestMetricsView speedTestId={activeTest.id} />
        </TabsContent>

        <TabsContent value="retention" className="mt-6">
          <SpeedTestRetentionChart speedTestId={activeTest.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
